import '@mfc/mfc-video-sdk/mfc-video-sdk.css'
import type { NextComponentType, NextPage, NextPageContext } from 'next'
import App, { AppContext, AppInitialProps } from 'next/app'
import Head from 'next/head'
import Router from 'next/router'
import { useCallback, useEffect, useRef, useState } from 'react'
import '../../public/css/app.css'
import '../../public/css/fonts.css'
import '../../public/css/keyframes.css'
import '../../public/css/palette.css'
import { ProcessVisitorResponse, ProcessVisitorUser, User } from '../../types'
import {
    type AppComponentProps,
    type LayoutProps,
    type SSRProps,
} from '../../types/mfc'
import { IApp, List, RouteHistoryItem } from '../../types/state'
import {
    getColorSchemeCookie,
    setColorScheme,
    setDarkMode,
} from '../actions/app'
import {
    startImageSecurityRefresh,
    stopImageSecurityRefresh,
} from '../actions/imageSecurity'
import { resumePMUnreadCount } from '../actions/message'
import { saveSettings } from '../actions/settings'
import { addFriend, bookmarkModel } from '../actions/user'
import { workerOnMessage, workerSendMessage } from '../actions/workerActions'
import { useAlertWebcamViewer } from '../components/Hooks/useAlertWebcamViewer'
import { useAppStateSelector } from '../components/Hooks/useAppState'
import { useHealthAppStateSelector } from '../components/Hooks/useHealthAppState'
import { usePassive } from '../components/Hooks/usePassive'
import { useSecureState } from '../components/Hooks/useSecureState'
import { useSettingSelector } from '../components/Hooks/useSettings'
import Layout from '../components/presentation/Layout'
import { COOKIE_PREF_CLI } from '../constants'
import {
    AppStateType,
    ColorScheme,
    DisplayUeopt,
    EventType,
    GatedDisplayModes,
    ListType,
    ModalEvent,
    SecureStateType,
    SettingsType,
    UserEvent,
} from '../constants/enums'
import {
    QUERY_PARAM_DEBUG,
    QUERY_PARAM_DISPLAY_TYPE,
    QUERY_PARAM_REDIRECT_FROM_WWW,
    QUERY_PARAM_SIGNUP,
    QUERY_PARAM_SKIP_INITIAL_POPUP,
} from '../constants/queryParams'
import { VIDEO_AVATAR_CLIP_CLASS } from '../constants/video'
import { getAffiliateQueries } from '../mfcLogic/api/account'
import { storeTipDefaults } from '../mfcLogic/api/tip'
import {
    authenticated,
    getAgeVerificationCookie,
    getUsername,
    isUnverifiedAccount,
    setAgeVerificationCookie,
} from '../mfcLogic/auth'
import { getVisitorInformation } from '../mfcLogic/getVisitorInformation'
import { UserAccess } from '../mfcLogic/userProperties/level'
import { videoPlayerReady } from '../mfcLogic/videoPlayerReady'
import {
    LAYOUTSTATE,
    LISTS,
    SECURESTATE,
    SETTINGSSTATE,
    USERSSTATE,
} from '../state'
import { getModalState } from '../state/getState'
import { setPMChatOpenState } from '../state/internal/pms'
import { onRouteChangeFullWindow } from '../state/internal/rooms'
import { idToSlug, slugToID } from '../state/internal/users'
import '../state/internal/windowObjects'
import {
    closeDialog,
    openAgeGateDialog,
    openAgeVerificationDialog,
    openGuestListDialog,
    openSignupGateIfForced,
} from '../state/modals/state.dialog'
import {
    openAuthenticationDrawer,
    openSignupDrawer,
    openTokensDrawer,
} from '../state/modals/state.drawer'
import { MUTATOR_MAP } from '../state/mutators'
import {
    pubSubList,
    pubSubModal,
    pubSubScreen,
    pubSubUsers,
} from '../state/pubSub'
import { setupSubscriptions } from '../state/pubSub/subscriptions'
import {
    pauseSocketConnection,
    resumeSocketConnection,
    updateAppState,
} from '../state/state.app'
import { openFloatingDebug } from '../state/state.debug'
import '../state/state.keys'
import { initListChanges } from '../state/state.lists'
import {
    setLastKnownScrollEndIndex,
    setLastKnownScrollOffset,
} from '../state/state.pm'
import { AppRouter } from '../state/state.router'
import { resolveFullWindow, setScreenOrientation } from '../state/state.screen'
import {
    initializeSidebarState,
    onRouteChangeSidebarType,
} from '../state/state.sidebar'
import { registerTimer, unregisterTimer } from '../state/state.timers'
import { trackAnalyticsEvent } from '../utilities/analytics'
import {
    isAndroid,
    isMobile as isClientMobile,
    isIDevice,
    isIpad,
} from '../utilities/browser'
import { isServer, ssrIsIOS, ssrIsMobile } from '../utilities/browserSSR'
import { setCookie } from '../utilities/cookies'
import { getWidth } from '../utilities/dimensions'
import { getGuestList, getGuestListSkip } from '../utilities/guestList'
import { nextCookieHandler } from '../utilities/nextCookieHandler'
import { scrollIntoViewIfNeeded } from '../utilities/polyfills'
import '../utilities/router'
import { routerPush } from '../utilities/router'

const BLUR_RADIUS = 60

if (typeof window !== 'undefined' && window._NEXTJS_FASH_REFRESH_HACK) {
    // SingletonRouter.events.on('routeChangeComplete', () => {
    AppRouter.events.onCompleted(() => {
        if (window._ROUTE_CHANGE_START_) {
            window._ROUTE_CHANGE_START_ = false
        }
    })
    // SingletonRouter.events.on('routeChangeStart', () => {
    AppRouter.events.onChange(() => {
        window._ROUTE_CHANGE_START_ = true
    })
}

const shouldSkipAgeVerification = () => {
    const { skip_oapopup } = AppRouter.route.visibleQuery //SingletonRouter?.router?.query?.skip_oapopup

    if (!skip_oapopup) {
        return false
    }

    return !!Number(skip_oapopup)
}

function ageVerification() {
    if (typeof window === 'undefined') return
    const hasAgeVerificationCookie = getAgeVerificationCookie()
    const skipAgeVerification = shouldSkipAgeVerification()

    if (getUsername() || hasAgeVerificationCookie || skipAgeVerification) {
        if (!hasAgeVerificationCookie) {
            setAgeVerificationCookie()
            closeDialog()
        }
    } else {
        openAgeVerificationDialog()
    }
}

function handleAgeGateRouting(
    ageGateActive: boolean,
    settings: GatedDisplayModes,
) {
    // restore saved route after successful age verification
    const RETURN_ROUTE_STORAGE_KEY = 'ageGateReturnRoute'
    const savedRoute = sessionStorage.getItem(RETURN_ROUTE_STORAGE_KEY)
    if (!ageGateActive && savedRoute) {
        sessionStorage.removeItem(RETURN_ROUTE_STORAGE_KEY)
        routerPush(savedRoute, { replaceState: true })
    }

    // redirect if user direct-loaded a restricted route
    const ageGateWithNoPreviews =
        ageGateActive && settings === GatedDisplayModes.hideEverything
    if (ageGateWithNoPreviews) {
        const currentRoute = AppRouter.route.visiblePage
        if (currentRoute !== '') {
            sessionStorage.setItem(
                RETURN_ROUTE_STORAGE_KEY,
                AppRouter.route.visibleRoute,
            )
            routerPush('/', { replaceState: true })
        }
    }
}

const HASH_MAP = {
    tokens: openTokensDrawer,
    login: openAuthenticationDrawer,
    signup: () => openSignupDrawer({ showPromoBanner: true }),
}

/** Grab all the stuff that we need on every page to properly render things on the server */
const getSsrData = (ctx: NextPageContext) => {
    const cookies = nextCookieHandler(ctx)
    const {
        user_id,
        user_lv,
        username,
        passcode,
        COLOR_SCHEME,
        SETTINGS_HOME__DISPLAY,
        fake_country,
        fake_region,
    } = cookies
    const ssrProps: SSRProps = {
        ssr: isServer(ctx),
        uid: user_id ? Number(user_id) : undefined,
        level: user_lv ? Number(user_lv) : undefined,
        displayType: SETTINGS_HOME__DISPLAY,
        username,
        passcode,
        authenticated: !!username && !!passcode,
        darkMode:
            COLOR_SCHEME === 'dark' ||
            (COLOR_SCHEME !== 'light' &&
                ctx?.req?.headers['sec-ch-prefers-color-scheme'] === 'dark'),
        isMobile: ssrIsMobile(ctx),
        isIOS: ssrIsIOS(ctx),
        fake_country,
        fake_region,
    }
    return ssrProps
}

//
//
// Marc: Strategies for removing the loading screen as quickly as possible
// depending on the page we're requesting.
//
//

// Marc: Here we can determine all the pages that don't need a loading page at
// all.
// const ssrStrategy = (router: Router | NextRouter) => () => {
//     const isMessagePage = router.pathname === '/message'
//     const isNewsPage = router.pathname === '/news'
//     const isProfilePage = router.pathname === '/profile'
//     const isRoomPage = router.pathname === '/room'

//     return isMessagePage || isNewsPage || isProfilePage || isRoomPage
//         ? false
//         : true
// }

// Marc: Here we can do it based on minimal list count ,useful for the home
// page.
const listItemsStrategy = (
    setLoadingState: () => void,
    listType = ListType.DEFAULT,
    count = 10,
) => {
    if (LISTS[listType]?.size() >= count) {
        setLoadingState()

        return () => {}
    }

    const callback = (list: List) => {
        if (list.size() < count) {
            return
        }

        setLoadingState()
        unsubscribe()
    }

    const unsubscribe = pubSubList.on(listType, callback)

    return unsubscribe
}

// Marc: Helper to deal with a few loading strategies...
const useLoadingStrategy = (
    setLoadingState: (orientationOverride?: boolean) => void,
    loading: boolean,
    connected: boolean,
    // router: Router | NextRouter,
) => {
    useEffect(() => {
        if (!loading) {
            return
        }

        if (!connected) {
            // Models seeded from server HTML can satisfy the home strategy
            // before the socket connects; every other route still waits for
            // the connection.
            if (AppRouter.route.visiblePage === '') {
                return listItemsStrategy(setLoadingState)
            }
            return
        }

        const route = AppRouter.route.visiblePage //router.asPath.toLowerCase()
        // const [, route] = asPath.split('/')
        const isMessagePage = route === 'message'
        const isNewsPage = route === 'news'
        const isProfilePage = route === 'profile'
        const isRoomPage = route === 'room'
        if (isRoomPage || isMessagePage || isProfilePage) {
            const floatingRoom = getModalState().floating_room
            // DG: this doesn't feel like a great spot, but we need an easy way
            // to tell the loading screen to stay a bit longer for rooms
            if (floatingRoom.show) {
                setLoadingState()
                return
            } else {
                return pubSubModal.on(ModalEvent.UPDATE_FLOATING_ROOM, () => {
                    if (getModalState().floating_room.show) {
                        setLoadingState()
                    }
                })
            }
        }
        if (isNewsPage && loading) {
            return pubSubScreen.on('state', (state) => {
                if (state.calculated) {
                    setLoadingState(false)
                }
            })
        }

        const isHomePage = AppRouter.route.visiblePage === ''

        if (!isHomePage) {
            setLoadingState()
            return
        }

        const unsubscribeListStrategy = listItemsStrategy(setLoadingState)

        return () => {
            unsubscribeListStrategy()
        }
    }, [setLoadingState, connected, loading])

    //
    // Marc: Depending on the user's settings and if they have no friends or
    // bookmarks, we could end up with an empty home page. Here, we will use the
    // "MODELS_LOADED" event to safe guard us against loading strategies that
    // depend on waiting for users to show up (e.g. the home page strategy).
    //
    useEffect(() => {
        const route = AppRouter.route.visiblePage
        const isRoomPage = route === 'room'
        const isMessagePage = route === 'message'
        if (!isRoomPage && !isMessagePage) {
            const callback = () => {
                setLoadingState(false)
            }

            return pubSubUsers.on(UserEvent.MODELS_LOADED, callback)
        }
    }, [setLoadingState])
}

/**
 * Scrolls the input into view on Android devices.
 * This is necessary because the keyboard will cover the input
 * and the user will not be able to see what they are typing.
 */
const useScrollInputIntoView = (socketConnected: boolean) => {
    const scrollTimer = useRef<number>(null)

    useEffect(() => {
        if (!isAndroid) return

        if (!socketConnected) {
            return () => cancelAnimationFrame(scrollTimer.current)
        }

        const input =
            document.activeElement.tagName === 'TEXTAREA' ||
            document.activeElement.tagName === 'INPUT'
        if (!input) {
            return () => cancelAnimationFrame(scrollTimer.current)
        }

        if (!document.activeElement.scrollIntoViewIfNeeded) {
            Element.prototype.scrollIntoViewIfNeeded = scrollIntoViewIfNeeded
        }
        cancelAnimationFrame(scrollTimer.current)
        scrollTimer.current = requestAnimationFrame(() => {
            document.activeElement.scrollIntoViewIfNeeded()
        })

        return () => {
            cancelAnimationFrame(scrollTimer.current)
        }
    }) // runs on every render
}

type MyAppInitialProps<P = Record<string, never>> = { pageProps: P }
type MyAppPropsType<P = Record<string, never>> = MyAppInitialProps<P> & {
    Component: NextComponentType<NextPageContext, any, AppComponentProps>
    // router: NextRouter
    __N_SSG?: boolean
    __N_SSP?: boolean
    __N_RSC?: boolean
}

type MyAppProps<P = Record<string, never>> = MyAppPropsType<P> & {
    visitorInformation: ProcessVisitorResponse
    page: NextPage<P>
}

const MyApp = (props: MyAppProps<SSRProps>) => {
    const {
        visitorInformation,
        // Component,
        pageProps,
        // page,
    } = props

    const timing = useRef<ReturnType<typeof Date.now>>(null)
    // const router =
    //     typeof window !== 'undefined' ? SingletonRouter.router : props.router

    const [loading, setLoadingState] = useState(true) //ssrStrategy(router))
    const [verifyingAge, setVerifyAgeState] = useState(true)
    const socketConnected = useHealthAppStateSelector('connected')
    const passiveMode = usePassive()

    const resolveLoadingState = useCallback(
        (orientationOverride = true) => {
            //
            // Marc (4/28/2023): We are calling "setScreenOrientation()" here
            // because the server always renders in portrait mode and we need to
            // first render the same thing on the client before setting the correct
            // orientation. The loading screen hides the initial mismatch. This was
            // previously being handled by cookies, but that causes issues when the
            // orientation has changed while off the app, and the cookie becomes
            // outdated.

            // setScreenOrientation({ overrideSkip: orientationOverride })
            // resolveFullWindow handles calling setScreenOrientation
            resolveFullWindow(orientationOverride)
            setTimeout(() => {
                setLoadingState(false)
            }, 0)
        },
        [setLoadingState],
    )

    const darkModeState = useAppStateSelector(AppStateType.APP, 'darkMode')
    const { ageGateActive: ageGateState } = useSecureState(
        SecureStateType.AGE_VERIFICATION,
    )

    useLoadingStrategy(
        resolveLoadingState,
        loading,
        socketConnected,
        // router,
    )

    const nsfw = useAppStateSelector(AppStateType.APP, 'nsfw')

    const {
        ssr = false,
        darkMode: ssrDarkMode = false,
        displayType,
        isMobile = isClientMobile(),
        isIOS,
        level: ssrLevel,
        uid: ssrUID,
        username,
        passcode,
    } = pageProps

    const [level, setUserLevel] = useState<UserAccess>(ssrLevel)
    const [uid, setUserID] = useState(ssrUID)

    // const windowWidth = useScreenStateSelector('windowWidth')
    const isAuthenticated = ssr ? pageProps.authenticated : authenticated()
    const darkMode = darkModeState ?? ssrDarkMode

    const layoutProps: LayoutProps = {
        loading: verifyingAge || loading,
        authenticated: isAuthenticated,
        isMobile,
        darkMode,
        username,
        level,
        uid,
        ssr,
    }
    // const componentProps: AppComponentProps = { ssr }

    const [display, saveDisplay] = useSettingSelector(
        SettingsType.DEFAULT,
        'display',
    )

    useEffect(() => {
        // after an iOS device restart or quitting Safari, the page can be a copy
        // rendered for another login state than the cookies now hold (a guest
        // copy next to login cookies reads as an unverified account), so
        // reload it once
        const SSR_COOKIE_MISMATCH_RELOAD_KEY = 'ssrCookieMismatchReload'
        if (ssr && pageProps.authenticated !== authenticated()) {
            // only a matching page clears the flag, so a mismatch the reload
            // doesn't fix can't keep reloading
            if (!sessionStorage.getItem(SSR_COOKIE_MISMATCH_RELOAD_KEY)) {
                sessionStorage.setItem(SSR_COOKIE_MISMATCH_RELOAD_KEY, '1')
                location.reload()
                return
            }
        } else {
            sessionStorage.removeItem(SSR_COOKIE_MISMATCH_RELOAD_KEY)
        }

        // before get visitor information set user preference cookie
        setCookie(COOKIE_PREF_CLI, 'app', {
            domain: 'myfreecams.com',
            path: '/',
            expires: new Date(new Date().getTime() + 1000 * 60 * 60 * 24 * 365),
        })

        getVisitorInformation(visitorInformation).then((props) => {
            const queries = getAffiliateQueries(true)

            const { cid } = queries

            if (cid && cid !== '3149' && display !== DisplayUeopt.preview) {
                saveDisplay(DisplayUeopt.preview)
            }

            if (!props) {
                const appState = { maintenanceMode: true }
                updateAppState(AppStateType.APP, appState)
                return
            }

            const { ageGateActive, user, settings } = props

            handleAgeGateRouting(ageGateActive, settings)

            setVerifyAgeState(false)

            const forceBlurs =
                pageProps?.fake_country !== undefined && props.ageGateActive
            startImageSecurityRefresh(forceBlurs)

            const isUnverified = isUnverifiedAccount(user)

            if (isUnverified) {
                console.warn(
                    'Account is unverified and needs to complete signup.',
                )
                updateAppState(AppStateType.APP, {
                    unverified: true,
                })
            }

            const forceAuthGate =
                !isAuthenticated &&
                !ageGateActive &&
                AppRouter.route.visibleQuery[QUERY_PARAM_SIGNUP] === 'true'
            if (forceAuthGate) {
                updateAppState(AppStateType.APP, { forceAuthGate })
            }

            const bypassInitialPopup =
                ageGateActive ||
                isUnverified ||
                AppRouter.route.visibleQuery[QUERY_PARAM_SKIP_INITIAL_POPUP] ===
                    'true'

            if (bypassInitialPopup) {
                // close the accept terms modal
                closeDialog()
                if (
                    ageGateActive &&
                    settings !== GatedDisplayModes.preview &&
                    !isUnverified
                ) {
                    openAgeGateDialog(settings)
                }
            } else {
                ageVerification()
            }
            openSignupGateIfForced()

            // age gate hashed routes
            if (!ageGateActive && !forceAuthGate) {
                const modalFn = HASH_MAP[AppRouter.route.hash]
                modalFn?.()
            }

            if (!user) {
                return
            }

            const { level, id } = user

            slugToID['me'] = id
            idToSlug[id] = 'me'
            const userState: User = {
                id,
                level,
                name: user.username,
                slug: user.username?.toLowerCase(),
                ...(user.token ? { token: user.token } : {}),
                ...(user.rewards_points
                    ? { rewardsPoints: user.rewards_points }
                    : {}),
            }
            USERSSTATE.users[id] = {
                ...userState,
                ...(USERSSTATE.users[id] || {}),
            }

            setUserLevel(level)
            setUserID(id)
            pubSubUsers.emit(UserEvent.ME_ID, id)
        })

        return () => {
            stopImageSecurityRefresh()
        }
    }, [])

    useEffect(() => {
        // warm the on-demand player chunk so opening a room doesn't wait on it
        videoPlayerReady().catch(() => undefined)
    }, [])

    useEffect(() => {
        //
        // Worker communication channel
        //
        const unsubWorkerOnMessage = workerOnMessage((event) => {
            unregisterTimer('workerTimeout')
            if (!event.data) return

            const { type } = event.data

            const mutator = MUTATOR_MAP[type]

            // handle packet events from the worker: state mutations and side effects
            mutator?.(event)
        })

        const reviveWorker = () => {
            //
            // Marc: Since iOS completely stops the app when its not in view
            // we will pause and resume the FCS socket accordingly
            //
            if (isIDevice) {
                if (document.hidden) {
                    pauseSocketConnection()
                }

                if (!document.hidden) {
                    pauseSocketConnection()
                    setTimeout(() => {
                        resumeSocketConnection()
                    }, 100)
                }
            }

            if (document.hidden) {
                timing.current = Date.now()
                return
            }

            if (timing.current === null) {
                timing.current = Date.now()
            }

            //
            // Marc: This is a backup in case the worker itself has died. It is
            // not related to the FCS socket, it related to the ping pong
            // messaging that we do between the frontend and worker. Search for
            // "EventType.PING"
            //
            registerTimer(
                'workerTimeout',
                () => {
                    location.reload()
                },
                10000,
                'timeout',
            )

            //
            // Marc: Lets figure out how long it's been hidden
            //
            const millisecondsHidden = Date.now() - timing.current

            //
            // Marc: Android is much more tolerant and we can wait around five
            // minutes to reset the app. Given the timeframe, it's best to just
            // reload
            //
            if (isAndroid && millisecondsHidden > 300000) {
                location.reload()
                return
            }
        }

        document.addEventListener('visibilitychange', reviveWorker)

        const updateDarkMode = (e: MediaQueryListEvent) => {
            if (
                SETTINGSSTATE.DEFAULT.colorScheme === ColorScheme.system &&
                !LAYOUTSTATE.layout.lockedColorScheme
            ) {
                setDarkMode(e.matches)
            }
        }
        const darkModeQuery = window.matchMedia('(prefers-color-scheme: dark)')
        darkModeQuery.addEventListener('change', updateDarkMode)

        //
        // Logic to restart app if WebWorker disconnects
        //
        registerTimer(
            'workerPing',
            () => {
                // console.log('Pinging Worker!')
                workerSendMessage(EventType.PING)
            },
            1000,
        )

        // const unsubSubscriptions = setupSubscriptions()

        initListChanges()

        // Initiate data flow
        workerSendMessage(EventType.READY)

        const darkModeCookie = getColorSchemeCookie()
        setColorScheme(darkModeCookie, true)
        // initializeSidebarState(windowWidth)

        return () => {
            document.removeEventListener('visibilitychange', reviveWorker)
            darkModeQuery.removeEventListener('change', updateDarkMode)
            unregisterTimer('listChanges')
            unregisterTimer('workerPing')
            unregisterTimer('workerTimeout')
            unsubWorkerOnMessage()
            // unsubSubscriptions()
        }
    }, [])

    useScrollInputIntoView(socketConnected)

    // support guest users saving settings
    // however, let logged in users be updated via ueopt when FCS connects
    useEffect(() => {
        if (
            AppRouter.route.visibleQuery[QUERY_PARAM_DISPLAY_TYPE] === 'preview'
        ) {
            saveSettings(SettingsType.DEFAULT, 'display', DisplayUeopt.preview)
            return
        }
        if (
            AppRouter.route.visibleQuery[QUERY_PARAM_DISPLAY_TYPE] === 'avatar'
        ) {
            saveSettings(SettingsType.DEFAULT, 'display', DisplayUeopt.default)
            return
        }
        if (level && level >= UserAccess.MEMBER_BASIC) return
        if (
            level === UserAccess.GUEST &&
            ((displayType as DisplayUeopt) === DisplayUeopt.default ||
                (displayType as DisplayUeopt) === DisplayUeopt.detailed ||
                (displayType as DisplayUeopt) === DisplayUeopt.preview ||
                (displayType as DisplayUeopt) === DisplayUeopt.text)
        )
            saveSettings(
                SettingsType.DEFAULT,
                'display',
                displayType as DisplayUeopt,
            )
    }, [displayType, level])

    useEffect(() => {
        if (verifyingAge || level !== UserAccess.GUEST) return

        const guestList = getGuestList()
        const friends = guestList?.friend || {}
        const bookmarks = guestList?.bookmark || {}

        Object.keys(friends).forEach((uid) => {
            if (uid) addFriend(Number(uid), false)
        })
        Object.keys(bookmarks).forEach((uid) => {
            if (uid) bookmarkModel(Number(uid), false)
        })
    }, [level, verifyingAge])

    useEffect(() => {
        // the age gate / verification flow owns the dialog slot until it lifts
        if (verifyingAge || ageGateState) return
        if (level === undefined || level === UserAccess.GUEST) return

        const guestList = getGuestList()
        const friends = guestList?.friend || {}
        const bookmarks = guestList?.bookmark || {}

        if (
            Object.keys(friends).length >= 1 ||
            Object.keys(bookmarks).length >= 1
        ) {
            const skipGuestList = getGuestListSkip()
            if (!skipGuestList) {
                openGuestListDialog()
            }
        }
    }, [level, verifyingAge, ageGateState])

    useEffect(() => {
        if (level === UserAccess.MEMBER_PREMIUM) {
            storeTipDefaults()
        }
    }, [level])

    useEffect(() => {
        const newAppState = {
            isMobile,
            isIOS,
            isIpad: !!isIpad,
            nsfw:
                nsfw || (process.env.NSFW === 'true' ? BLUR_RADIUS : undefined),
            passcode: passcode || 'guest',
            authenticated: isAuthenticated,
        } as IApp
        // if (username) newAppState.name = username
        // if (typeof level === 'number') newAppState.level = level
        // if (typeof uid === 'number') newAppState.uid = uid
        updateAppState(AppStateType.APP, newAppState)
    }, [isMobile, isIOS, isAuthenticated, passcode, username, uid, level])

    useAlertWebcamViewer()

    return (
        <>
            <Head>
                <meta charSet="utf-8" />
                <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
                <meta
                    name="viewport"
                    content="width=device-width,initial-scale=1,minimum-scale=1,maximum-scale=1,user-scalable=no,viewport-fit=cover"
                />
                <meta name="RATING" content="RTA-5042-1996-1400-1577-RTA" />
                <meta name="Rating" content="mature" />
                <meta
                    httpEquiv="pics-Label"
                    content='(pics-1.1 "http://www.icra.org/pics/vocabularyv03/" l gen true for "http://myfreecams.com" r (n 3 s 3 v 0 l 2 oa 2 ob 2 oc 0 od 0 oe 0 of 2 og 0 oh 0 c 3) gen true for "http://www.myfreecams.com" r (n 3 s 3 v 0 l 2 oa 2 ob 2 oc 0 od 0 oe 0 of 2 og 0 oh 0 c 3))'
                />
                <meta
                    name="google-site-verification"
                    content="B5a-T5liVrhXq64BsRyFtNZKj5XmPGBC8yLFfRCOm_Q"
                />
                {darkMode ? (
                    <meta name="theme-color" content="rgb(30, 30, 30)" />
                ) : (
                    <meta name="theme-color" content="#FFFFFF" />
                )}
                {!passiveMode && <title>MyFreeCams App</title>}
                <meta
                    name="description"
                    content="MyFreeCams is the original free webcam community for adults, featuring live video chat with thousands of models, cam girls, amateurs and female content creators!"
                />
                <meta
                    name="Keywords"
                    content="MyFreeCams,free,cams,webcams,live,video,chat,sex,girls,nude,amateurs,models,adults,community,camgirls,pornstars,camming,sound"
                />
                <meta version={process.env.APP_VERSION} />
                <meta data-env={process.env.NODE_ENV} />
                <link
                    href="http://www.myfreecams.com/labels.rdf"
                    type="application/rdf+xml"
                    title="ICRA labels"
                    rel="meta"
                />
                <link rel="manifest" href="/static/manifest.json" />
                <link
                    rel="icon"
                    sizes="any"
                    href="https://newassets.mfcimg.com/static/icons/favicon.ico"
                />
                <link
                    rel="icon"
                    type="image/png"
                    sizes="32x32"
                    href="/static/icons/favicon-32x32.png"
                />
                <link
                    rel="apple-touch-icon"
                    sizes="180x180"
                    href="/static/icons/apple-touch-icon-180x180.png"
                />
                <link
                    rel="apple-touch-icon"
                    sizes="152x152"
                    href="/static/icons/apple-touch-icon-152x152.png"
                />
                <link
                    rel="apple-touch-icon"
                    sizes="120x120"
                    href="/static/icons/apple-touch-icon-120x120.png"
                />
                <link
                    rel="mask-icon"
                    href="/static/icons/safari-pinned-tab.svg"
                />
                <meta name="msapplication-TileColor" content="#FFFFFF" />
                <meta
                    name="msapplication-config"
                    content="/static/icons/browserconfig.xml"
                />
                {ageGateState && (
                    <style>
                        {`.mfc-image-asset {
                                filter: blur(2.5px) !important;
                            }
                            .mfc-age-gate img {
                                filter: blur(2.5px) !important;
                            }
                            .mfc-restricted-content {
                                filter: blur(2.5px) !important;
                            }
                            .mfc-restricted-strict {
                                filter: blur(14px) !important;
                                /* the blur spreads ~40px past the box; in the
                                   Portrait room the Share slider sits right
                                   under the transparent compose bar */
                                clip-path: inset(0);
                            }
                            video:not(.${VIDEO_AVATAR_CLIP_CLASS}) {
                                filter: blur(36px) !important;
                            }
                            .MFCShare_inChat_img {
                                display: none !important;
                            }
                            .MFCShare_inChat_title b {
                                display: none !important;
                            }`}
                    </style>
                )}
                {/* the developer NSFW blur covers video avatar clips too;
                    only the age gate's blur above leaves them clear */}
                {nsfw && (
                    <style>
                        {`img, video {
                                filter: blur(${nsfw}px) !important;
                            }`}
                    </style>
                )}
            </Head>
            <Layout {...layoutProps}>
                {/* <Component {...componentProps} /> */}
            </Layout>
        </>
    )
}

/**
 * Optional method that allows for overwriting and full control of the `page`
 * initialization. This allows for keeping state between navigation,
 * custom error handling, injecting additional data.
 *
 * Using `getInitialProps` disables the ability to perform automatic static
 * optimization, causing every page in your app to be server-side rendered.
 */
MyApp.getInitialProps = async (appContext: AppContext) => {
    // calls page's `getInitialProps` and fills `appProps.pageProps`
    const props = await App.getInitialProps(appContext)

    const { pageProps, ...appProps } = props

    const ctx = appContext.ctx as NextPageContext & {
        req: {
            ip: string
            visitorInformation?: ProcessVisitorResponse
        }
        me: User
    }

    const visitorInformation = ctx.req?.visitorInformation
    const { user = {} as ProcessVisitorUser } = visitorInformation || {}

    const {
        rewards_points: rewardsPoints,
        access_level: level = 0,
        username = '',
        id = 0,
        tokens,
        token,
        // status: 'premium',
        // emails
    } = user

    const name = username || ''
    const slug = name.toLowerCase() || 'me'
    const me = {
        rewardsPoints,
        tokens,
        token,
        level,
        slug,
        name,
        id,
    }

    ctx.me = visitorInformation ? me : null

    // grab Authentication status, SSR, isMobile, and Dark Mode since thats needed everywhere
    const ssrProps = getSsrData(appContext.ctx)
    // console.log(
    //     '[_app.getInitialProps] return props',
    //     appProps,
    //     pageProps,
    //     ssrProps,
    // )

    const initialProps: AppInitialProps & {
        visitorInformation: ProcessVisitorResponse
    } = {
        ...appProps,
        visitorInformation,
        pageProps: {
            ...(Object.keys(pageProps).length > 0 ? pageProps : {}),
            ...ssrProps,
        },
    }

    AppRouter.init(appContext.router.asPath)

    return initialProps
}

AppRouter.events.onReady((initialPath) => {
    const initialOptions = {
        initial: true,
        // ensure we replace the nextjs history state so a user doesn't
        // hit "back" and find themselves with weird url state
        replaceState: true,
    }

    if (typeof window !== 'undefined') {
        // console.log('[onReady]', Router.router.onPopState)
        setupSubscriptions()
        initializeSidebarState(getWidth())

        // safe check for vitest
        if (Router && Router.router) {
            // remove nextjs's onPopState listener so we don't have weird collisions
            window.removeEventListener('popstate', Router.router.onPopState)
        }

        window.addEventListener('popstate', (event) => {
            // if event.state is null or missing our routes (because initial load will have an entry from nextjs),
            // we should just load the initial path
            const { visibleRoute = initialPath, baseRoute = initialPath } =
                (event.state || {}) as RouteHistoryItem

            routerPush(visibleRoute, {
                baseRoute: baseRoute,
                replaceState: true,
            })
        })
        /* NOTE: if we only call setScreenOrientation here ios PWA will report the wrong bottom inset size and
           calculations will be wrong (height will be ~34px larger than it should be) */
        setScreenOrientation({ overrideSkip: true })

        // handle room redirects from www
        if (
            AppRouter.route.visibleQuery[QUERY_PARAM_REDIRECT_FROM_WWW] === '1'
        ) {
            trackAnalyticsEvent({
                action: `RedirectFrom_WWW`,
            })
            if (AppRouter.route.hash === 'homepage' || !AppRouter.route.hash) {
                return routerPush(initialPath, initialOptions)
            } else if (AppRouter.route.hash) {
                return routerPush(`/room/${AppRouter.route.hash}`, {
                    replaceState: true,
                    // can't be `initial: true` as we don't have this user in our initial state from server
                })
            }
        }
    }
    routerPush(initialPath, initialOptions)

    if (AppRouter.route.visibleQuery[QUERY_PARAM_DEBUG]) {
        openFloatingDebug()
        console.log('[onReady] Debug mode enabled', {
            visibleQuery: AppRouter.route.visibleQuery,
        })
    }
})

AppRouter.events.onChange((routeInfo) => {
    if (typeof window === 'undefined') {
        return
    }

    // redirect if age gate is active and user tries to access restricted route
    const { ageVerification, displayConfig } = SECURESTATE
    const ageGateWithNoPreviews =
        ageVerification.ageGateActive &&
        displayConfig?.settings === GatedDisplayModes.hideEverything
    if (ageGateWithNoPreviews && routeInfo.pendingRoute !== '') {
        routerPush('/', { replaceState: true })
        return
    }

    if (
        routeInfo.currentRoute !== 'messages' &&
        routeInfo.currentRoute !== 'message' &&
        (routeInfo.pendingRoute === 'messages' ||
            routeInfo.pendingRoute === 'message')
    ) {
        setLastKnownScrollEndIndex(0)
        setLastKnownScrollOffset(0)
    }

    if (routeInfo.currentRoute === 'message') {
        const currentRouteID = slugToID[routeInfo.currentSlug.toLowerCase()]
        resumePMUnreadCount(currentRouteID)
        setPMChatOpenState(currentRouteID, false)
    }

    if (!routeInfo.currentRoute && routeInfo.pendingRoute !== '') {
        updateAppState(AppStateType.APP, { hideFeaturedModels: true })
    }
})

AppRouter.events.onCompleted((routeInfo) => {
    if (typeof window === 'undefined') {
        return
    }

    // console.log('[onCompleted] callback', routeInfo)
    onRouteChangeSidebarType(routeInfo.fullRoute)
    onRouteChangeFullWindow(routeInfo.fullRoute)
})

export default MyApp

export const thisIsAnUnusedExport =
    'this export only exists to disable fast refresh for this file'
